import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import axios from '../../api/axios';


export const getAllProducts = createAsyncThunk(
  'product/getAllProducts',
  async (thunkAPI) => {
    const response = await axios.get('/products');
    return response.data;
  })

export const getProductsByCollection = createAsyncThunk(
  'product/getProductsByCollection',
  async (collection, thunkAPI) => {
    const response = await axios.get('/products');
    const filtered = response.data.filter(p => p.company.toLowerCase() === collection.toLowerCase());
    return filtered;
  })


const productSlice = createSlice({
  name: 'product',
  initialState: {
    products: [],
    filteredProducts: [],
    images: [],
    curIndex: 0,
    slideIndex: 0,
    productId: 0,
    product: [],
    loading: true,
    error: false,
    errMsg: '',
    filter: { company: '', color: '' },
    containFilters: [],
    sort: 'newest',
    colors: [],
    brands: [],
    collection: []
  },
  reducers: {
    getProducts: (state, action) => {
      state.products = action.payload.products
      state.loading = false
    },
    setError: (state, action) => {
      state.loading = false
      state.error = true
      state.errMsg = action.payload.err
    },
    getFilteredProducts: (state, action) => {
      // Filter by health concern instead of gender for Ayurvedic medicines
      const filterValue = action.payload.healthConcern || action.payload.gender;
      if (filterValue) {
        state.filteredProducts = state.products.filter((item) => {
          const category = item.categories && item.categories.at(-1);
          if (!category) return false;
          
          // Check healthConcern (new structure) or gender (old structure for compatibility)
          const concerns = category.healthConcern || category.gender || [];
          return concerns.some(concern => 
            concern.toLowerCase().includes(filterValue.toLowerCase())
          );
        });
      } else {
        state.filteredProducts = state.products;
      }
    },
    changeImage: (state, action) => {
      // CHANGE PREVIEW IMG ON CLICK
      state.curIndex = action.payload.index
    },
    prevPreview: (state, action) => {
      if (state.curIndex < 1) {
        state.curIndex = 0
      } else {
        state.curIndex -= 1
      }
    },
    nextPreview: (state, action) => {
      if (state.curIndex > (state.images.length - 2)) {
        state.curIndex = state.images.length - 1
      } else {
        state.curIndex += 1
      }
    },
    prevSlide: (state, action) => {
      if (state.slideIndex < 1) {
        state.slideIndex = 0
      } else {
        state.slideIndex -= 1
      }
    },
    nextSlide: (state, action) => {
      if (state.slideIndex > (state.images.length - 2)) {
        state.slideIndex = state.images.length - 1
      } else if ((state.slideIndex > (state.images.length - 3) && window.innerWidth > 640)) {
        state.slideIndex = state.images.length - 2
      }
      else {
        state.slideIndex += 1
      }
    },
    getProductItem: (state, action) => {
      state.productId = action.payload.productId
      state.product = state.products.filter((item) => item._id === state.productId)[0]
      state.images = state.error ? null : state.product.img
    },
    getFilters: (state, action) => {
      // GET LIST OF ALL FORMS (capsule, tablet, etc.) FROM PRODUCTS
      const products = state.filteredProducts.length > 0 ? state.filteredProducts : state.products;
      const allForms = products.map(item => {
        const category = item.categories && item.categories.at(-1);
        return category && category.form ? category.form : [];
      });
      state.colors = Array.from(new Set(allForms.flat())).sort();
      
      // GET LIST OF ALL BRANDS/COMPANIES FROM PRODUCTS 
      state.brands = Array.from(new Set(products.map(item => item.company))).sort();
    },
    selectFilters: (state, action) => {
      state.filter = action.payload.filter

      // return an array of true and false based on if the product contains a filter
      const products = state.filteredProducts.length < 1 ? state.products : state.filteredProducts;
      
      if (state.filter.color === '' && state.filter.company === '') {
        state.containFilters = products.map(item => true);
      } else if (state.filter.company !== '' && state.filter.color === '') {
        state.containFilters = products.map(item => 
          item.company.toLowerCase().includes(state.filter.company.toLowerCase())
        );
      } else {
        state.containFilters = products.map(item => {
          let matches = true;
          
          // Check company filter
          if (state.filter.company) {
            matches = matches && item.company.toLowerCase().includes(state.filter.company.toLowerCase());
          }
          
          // Check form filter (color field is used for form in new structure)
          if (state.filter.color) {
            const category = item.categories && item.categories.at(-1);
            const forms = category && category.form ? category.form : [];
            matches = matches && forms.some(form => 
              form.toLowerCase().includes(state.filter.color.toLowerCase())
            );
          }
          
          return matches;
        });
      }
    },
    selectSort: (state, action) => {
      state.sort = action.payload.sort
      let items = state.filteredProducts.length < 1 ? state.products : state.filteredProducts

      switch (action.payload.sort) {
        case 'newest':
          items = (state.filteredProducts.length < 1 ? state.products : state.filteredProducts).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
          break;
        case 'asc':
          items = (state.filteredProducts.length < 1 ? state.products : state.filteredProducts).sort((a, b) => a.discountPrice - b.discountPrice)
          break;
        case 'desc':
          items = (state.filteredProducts.length < 1 ? state.products : state.filteredProducts).sort((a, b) => b.discountPrice - a.discountPrice)
          break;
        default:
          // eslint-disable-next-line
          items = (state.filteredProducts.length < 1 ? state.products : state.filteredProducts).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
          break;
      }
    }
  },
  extraReducers: {
    [getAllProducts.pending]: (state) => {
      state.loading = true
    },
    [getAllProducts.fulfilled]: (state, { payload }) => {
      state.loading = false
      state.products = payload
      state.containFilters = state.products.map(item => true)
    },
    [getAllProducts.rejected]: (state, action) => {
      state.loading = false
      state.error = true
      state.errMsg = action.error.message
    },
    [getProductsByCollection.pending]: (state) => {
      state.loading = true
    },
    [getProductsByCollection.fulfilled]: (state, { payload, meta, collection }) => {
      state.loading = false
      state.collection = payload
    },
    [getProductsByCollection.rejected]: (state, action) => {
      state.loading = false
      state.error = true
      state.errMsg = action.error.message
    },
  }
}
)

export const { getProducts, setError, getFilteredProducts, changeImage, prevPreview, nextPreview, prevSlide, nextSlide, getProductItem, quantityCount, selectFilters, selectSort, getFilters } = productSlice.actions;
export default productSlice.reducer;